import React, { useState, useCallback } from "react";
import { useIntl } from "react-intl";
import VirtualKeyboard from "../../../../components/VirtualKeyboard";
import { ModalContainer, ModalContent, ModalHeader, ModalBody, WarningIcon, MessageText, ReasonInput, ButtonContainer, CancelButton, ConfirmButton, ErrorText, CharCounter } from "./styles";

// Constantes de validação
const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 100;

/**
 * Modal de confirmação para assumir posição de totem em uso
 * @param {boolean} isOpen - Controla visibilidade do modal
 * @param {function} onClose - Callback para fechar modal (cancela operação)
 * @param {function} onConfirm - Callback ao confirmar (recebe reason como parâmetro)
 * @param {number} position - Número da posição que está em uso
 */
const OverrideModal = ({ isOpen, onClose, onConfirm, position }) => {
  const intl = useIntl();
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const handleConfirm = () => {
    // Validação: mínimo de caracteres definido pela constante
    if (!reason.trim()) {
      setError(intl.formatMessage({ id: "totem.override.reason_required" }));
      return;
    }
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(intl.formatMessage({ id: "totem.override.reason_too_short" }));
      return;
    }
    
    // Limpa estado e executa callback
    onConfirm(reason.trim());
    setReason("");
    setError("");
    setIsKeyboardVisible(false);
  };

  const handleClose = () => {
    // Limpa estado e fecha
    setReason("");
    setError("");
    setIsKeyboardVisible(false);
    onClose();
  };

  const handleReasonFocus = useCallback(() => {
    setIsKeyboardVisible(true);
  }, []);

  const handleKeyPress = useCallback((key) => {
    setReason((prev) => {
      if (prev.length >= MAX_REASON_LENGTH) return prev;
      return prev + key;
    });
    setError("");
  }, []);

  const handleBackspace = useCallback(() => {
    setReason((prev) => prev.slice(0, -1));
  }, []);

  const handleClear = useCallback(() => {
    setReason("");
    setError("");
  }, []);

  const handleKeyboardClose = useCallback(() => {
    setIsKeyboardVisible(false);
  }, []);

  const handleEnterKey = useCallback(() => {
    // Enter no teclado virtual pode fechar o teclado ou confirmar
    setIsKeyboardVisible(false);
  }, []);

  return (
    <ModalContainer
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        overlay: {
          zIndex: 999999,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          paddingTop: "80px",
        },
        content: {
          position: "relative",
          top: "auto",
          left: "auto",
          right: "auto",
          bottom: "auto",
          margin: "0 20px",
          transform: "none",
          borderRadius: "12px",
          padding: 0,
          border: "none",
          maxWidth: "500px",
          width: "100%",
          maxHeight: "calc(100vh - 160px)",
          overflow: "auto",
        },
      }}
    >
      <ModalContent>
        <ModalHeader>
          <WarningIcon>⚠️</WarningIcon>
          <h2>{intl.formatMessage({ id: "totem.override.title" })}</h2>
        </ModalHeader>
        
        <ModalBody>
          <MessageText>
            {intl.formatMessage(
              { id: "totem.override.message" },
              { position }
            )}
          </MessageText>
          
          <label htmlFor="override-reason">
            {intl.formatMessage({ id: "totem.override.reason_label" })} <span style={{ color: "#ff6b6b" }}>*</span>
          </label>
          
          <ReasonInput
            id="override-reason"
            as="textarea"
            rows={4}
            value={reason}
            readOnly
            onFocus={handleReasonFocus}
            onClick={handleReasonFocus}
            onTouchStart={handleReasonFocus}
            placeholder={intl.formatMessage({ id: "totem.override.reason_placeholder" })}
            maxLength={MAX_REASON_LENGTH}
            hasError={!!error}
          />
          
          <CharCounter $isInvalid={reason.length < MIN_REASON_LENGTH}>
            {reason.length}/{MAX_REASON_LENGTH}
          </CharCounter>
          
          {error && <ErrorText>{error}</ErrorText>}
          
          <ButtonContainer>
            <CancelButton onClick={handleClose}>
              {intl.formatMessage({ id: "cancel" })}
            </CancelButton>
            <ConfirmButton onClick={handleConfirm}>
              {intl.formatMessage({ id: "totem.override.confirm" })}
            </ConfirmButton>
          </ButtonContainer>
        </ModalBody>
      </ModalContent>

      <VirtualKeyboard
        isVisible={isKeyboardVisible}
        onKeyPress={handleKeyPress}
        onBackspace={handleBackspace}
        onClear={handleClear}
        onClose={handleKeyboardClose}
        onEnter={handleEnterKey}
        value={reason}
      />
    </ModalContainer>
  );
};

export default OverrideModal;
